import cn from 'classnames';
import Checkbox from '@creditflowresearch/uilib/Checkbox';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import Flex from '@creditflowresearch/uilib/Flex';
import BadgeButton from '@/creditflowresearch/ui/common/components/molecules/BadgeButton';
import FilterSection from '@/creditflowresearch/ui/common/components/templates/FilterSection';
import FilterSectionHeader from '@/creditflowresearch/ui/common/components/organisms/FilterSectionHeader';
import FilterGroup from '@framework/ui/Organisms/Filters/components/FilterGroup';
import FilterItem, { filterItemStyles } from '@/creditflowresearch/ui/common/components/FilterItem';
import {
  FilterSectionIds,
} from '@/creditflowresearch/ui/components/corporate-bond-screener/Sidebar/Sections/constants';
import {
  toggle,
  toggleMany,
} from '@/creditflowresearch/application/BondScreener/selection';
import useRegionsCountriesSection from './useRegionsCountriesSection';

import styles
  from '@/creditflowresearch/ui/components/corporate-bond-screener/Sidebar/Sections/styles/twoLevelFilter.module.scss';

const MARKET_TOOLTIP = (
  <span className={ styles.presetButtonTooltip }>
    Bulk select all relevant options from the country list.
    This selection will not override any options you have already pre-selected.
  </span>
);
const MATCHING_TRANCHES_TOOLTIP = 'No matching tranches with current filters';

const RegionsCountries = () => {
  const {
    regionItems,
    selected,
    updateSelection,
    markets,
    isAllSelected,
    isIndeterminate,
    toggleAll,
  } = useRegionsCountriesSection();

  return (
    <FilterSection
      sectionId={ FilterSectionIds.RegionsCountries }
      header={ (
        <FilterSectionHeader
          title="ISSUER REGION & COUNTRIES"
          leftSlot={ (
            <Checkbox
              checked={ isAllSelected }
              indeterminate={ isIndeterminate }
              onChange={ toggleAll }
              supportIndeterminate
              dataTest="regionsFiltersAllCheckbox"
            />
          ) }
        />
      ) }
    >
      <Flex gap={ 10 }>
        { markets.map((segment) => (
          <BadgeButton
            key={ segment.id }
            label={ segment.label }
            value={ segment.id }
            isActive={ segment.isActive }
            onClick={ () => updateSelection(toggleMany(selected, segment.ids)) }
            tooltip={ MARKET_TOOLTIP }
            dataTest={ `market-${ segment.label }` }
            className={ cn(styles.presetButton, { [styles.presetButtonActive]: segment.isActive }) }
            tooltipClassName={ styles.presetButtonTooltipContainer }
            tooltipPlacement={ 'bottom-start' }
          />
        )) }
      </Flex>

      <div className={ styles.separator } />

      { regionItems.map((region) => (
        <FilterGroup
          key={ region.id }
          groupId={ region.id }
          isExpanded={ false }
          showArrow={ region.children.length > 0 }
          itemsCount={ region.enabledIds.length }
          selectedItemsCount={ region.selectedCount }
          checkboxComponent={ (
            <Checkbox
              checked={ region.allIds.length > 0 && region.totalSelectedCount === region.allIds.length }
              indeterminate={ region.totalSelectedCount > 0 && region.totalSelectedCount < region.allIds.length }
              onChange={ () => updateSelection(toggleMany(selected, region.allIds)) }
              supportIndeterminate
              label={ region.name }
              dataTest={ `region-${ region.id }` }
            />
          ) }
        >
          { region.children.map((country) => (
            <FilterItem
              key={ country.id }
              className={ cn(filterItemStyles.filterItem, filterItemStyles.filterNestedItem) }
              isExpandable={ false }
            >
              <Checkbox
                checked={ selected.includes(country.id) }
                disabled={ country.disabled }
                onChange={ () => updateSelection(toggle(selected, country.id)) }
                label={ country.disabled ? (
                  <Tooltip content={ MATCHING_TRANCHES_TOOLTIP }>{ country.name }</Tooltip>
                ) : country.name }
                dataTest={ `country-${ country.id }` }
              />
            </FilterItem>
          )) }
        </FilterGroup>
      )) }
    </FilterSection>
  );
};

export default RegionsCountries;
